'use client';

import React, { useMemo } from 'react';
import Image from 'next/image';
import { FaShoppingBag, FaCaravan, FaCampground, FaSolarPanel, FaUtensils, FaExternalLinkAlt } from 'react-icons/fa';
import type { Feature, ShopProduct } from '../types';
import PreviewAddToCart, { useCaPreview } from './store/PreviewAddToCart';

// ── Shopify store base URL ─────────────────────────────────────
const STORE_URL = 'https://campingaustralia.com.au';
const API_BASE = 'https://app.trip-planner.com.au/api';

// ── Click tracking ─────────────────────────────────────────────

function trackProductClick(
  product: ShopProduct,
  entityId?: number,
  entityName?: string,
  campsiteType?: string,
) {
  try {
    const payload = JSON.stringify({
      productTitle: product.title,
      productHandle: product.handle,
      productType: product.productType,
      entityId: entityId,
      entityName: entityName || '',
      campsiteType: campsiteType || '',
    });

    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(`${API_BASE}/products/track-click`, blob);
    } else {
      fetch(`${API_BASE}/products/track-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Never block the user
  }
}

// ── Fallback collection links ──────────────────────────────────

interface ProductLink {
  label: string;
  href: string;
  icon: React.ReactNode;
}

interface GearProfile {
  heading: string;
  description: string;
  links: ProductLink[];
}

function getGearProfile(features: Feature[], entityType?: string): GearProfile {
  // Use entityType first for accurate type detection
  const isCaravan = entityType === 'caravan_park' || entityType === 'caravan';
  const isFreeCamping = entityType === 'free_camping';
  const isAccom = entityType === 'accom' || entityType === 'accommodation';
  const isTours = entityType === 'tours';

  // Fallback to feature-based detection only if no entityType
  const featureDescs = new Set(
    features.map((f) => (f.desc || '').toLowerCase().trim())
  );
  const featureCaravan = !entityType && featureDescs.has('caravan parks');
  const featureFree = !entityType && featureDescs.has('free camping');
  const featureCamping = !entityType && featureDescs.has('camping');

  if (isCaravan || featureCaravan) {
    return {
      heading: 'Caravan & RV Gear',
      description: 'Top picks for your caravan park stay',
      links: [
        { label: 'Caravan Accessories', href: `${STORE_URL}/collections/caravans`, icon: <FaCaravan className="text-accent" /> },
        { label: 'Portable Fridges', href: `${STORE_URL}/collections/portable-chest-fridge-freezer`, icon: <FaShoppingBag className="text-accent" /> },
        { label: 'Power & Solar', href: `${STORE_URL}/collections/electrical-battery-solar`, icon: <FaSolarPanel className="text-amber-500" /> },
        { label: 'Camp Cooking', href: `${STORE_URL}/collections/camp-cooking-equipment`, icon: <FaUtensils className="text-red-500" /> },
      ],
    };
  }

  if (isFreeCamping || featureFree) {
    return {
      heading: 'Free Camping Essentials',
      description: 'Be self-sufficient out in the bush',
      links: [
        { label: 'Tents & Swags', href: `${STORE_URL}/collections/tents`, icon: <FaCampground className="text-green-600" /> },
        { label: 'Solar & Batteries', href: `${STORE_URL}/collections/electrical-battery-solar`, icon: <FaSolarPanel className="text-amber-500" /> },
        { label: 'Camp Cooking', href: `${STORE_URL}/collections/camp-cooking-equipment`, icon: <FaUtensils className="text-red-500" /> },
        { label: 'Toilet & Shower', href: `${STORE_URL}/collections/camping-toilet-and-shower`, icon: <FaShoppingBag className="text-accent" /> },
      ],
    };
  }

  if (isAccom) {
    return {
      heading: 'Travel Essentials',
      description: 'Make your stay comfortable',
      links: [
        { label: 'Camp Furniture', href: `${STORE_URL}/collections/camping-furniture`, icon: <FaShoppingBag className="text-accent" /> },
        { label: 'Portable Fridges', href: `${STORE_URL}/collections/portable-chest-fridge-freezer`, icon: <FaShoppingBag className="text-accent" /> },
        { label: 'Camping Appliances', href: `${STORE_URL}/collections/camping-appliances`, icon: <FaSolarPanel className="text-amber-500" /> },
        { label: 'Caravan Accessories', href: `${STORE_URL}/collections/caravans`, icon: <FaCaravan className="text-accent" /> },
      ],
    };
  }

  if (isTours) {
    return {
      heading: 'Adventure Gear',
      description: 'Gear up for your next adventure',
      links: [
        { label: 'Camp Furniture', href: `${STORE_URL}/collections/camping-furniture`, icon: <FaShoppingBag className="text-accent" /> },
        { label: 'Camping Appliances', href: `${STORE_URL}/collections/camping-appliances`, icon: <FaSolarPanel className="text-amber-500" /> },
        { label: 'Tents & Swags', href: `${STORE_URL}/collections/tents`, icon: <FaCampground className="text-green-600" /> },
        { label: 'Camp Cooking', href: `${STORE_URL}/collections/camp-cooking-equipment`, icon: <FaUtensils className="text-red-500" /> },
      ],
    };
  }

  return {
    heading: featureCamping ? 'Camping Gear' : 'Outdoor Gear',
    description: 'Everything you need for your trip',
    links: [
      { label: 'Tents & Swags', href: `${STORE_URL}/collections/tents`, icon: <FaCampground className="text-green-600" /> },
      { label: 'Camp Cooking', href: `${STORE_URL}/collections/camp-cooking-equipment`, icon: <FaUtensils className="text-red-500" /> },
      { label: 'Camping Furniture', href: `${STORE_URL}/collections/camping-furniture`, icon: <FaShoppingBag className="text-accent" /> },
      { label: 'Portable Fridges', href: `${STORE_URL}/collections/portable-chest-fridge-freezer`, icon: <FaShoppingBag className="text-accent" /> },
    ],
  };
}

// ── Price formatter ────────────────────────────────────────────

function formatPrice(price: number | null): string {
  if (price === null || price === undefined) return '';
  return `$${price.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ── Product Card ───────────────────────────────────────────────

function ProductCard({ product, onTrack, preview }: { product: ShopProduct; onTrack?: () => void; preview?: boolean }) {
  const hasDiscount = product.compareAtPrice && product.price && product.compareAtPrice > product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.compareAtPrice! - product.price!) / product.compareAtPrice!) * 100)
    : 0;

  // The card is a div so the (preview-only) add-to-cart button can sit inside
  // it without nesting a button in a link. Visually unchanged when off.
  return (
    <div
      className={preview
        ? 'ca-gear-card group flex flex-col'
        : 'group block bg-white rounded-lg border border-gray-100 hover:border-[#e3f2fd] hover:shadow-md transition-all overflow-hidden'}
    >
    <a
      href={preview ? product.productUrl.replace(/^https:\/\/campingaustralia\.com\.au/, '') : product.productUrl}
      target={preview ? undefined : '_blank'}
      rel={preview ? undefined : 'noopener noreferrer'}
      onClick={onTrack}
      className={preview ? 'block flex-1 no-underline' : 'block'}
    >
      {/* Product Image */}
      <div className={preview ? 'ca-gear-media relative w-full aspect-square' : 'relative w-full aspect-square bg-gray-100'}>
        {product.imageUrl ? (
          <Image
            src={product.imageUrl}
            alt={product.title}
            fill
            unoptimized
            className="object-contain p-2 group-hover:scale-105 transition-transform duration-200"
            sizes="(max-width: 768px) 50vw, 200px"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <FaShoppingBag size={32} />
          </div>
        )}
        {!preview && hasDiscount && discountPercent > 0 && (
          <span className={`absolute top-2 left-2 ${preview ? 'bg-[#d7263d]' : 'bg-red-500'} text-white text-xs font-bold px-2 py-0.5 rounded`}>
            -{discountPercent}%
          </span>
        )}
      </div>

      {/* Product Info */}
      <div className={preview ? 'ca-gear-body' : 'p-3'}>
        <p className={preview ? 'ca-gear-vendor' : 'text-xs text-gray-500 mb-0.5'}>{product.vendor}</p>
        <h4 className="ca-gear-title text-sm font-medium text-gray-900 group-hover:text-[#1558b0] line-clamp-2 leading-tight min-h-[2.5rem]">
          {product.title}
        </h4>
        <div className="mt-2 flex items-baseline gap-2">
          {product.price !== null && (
            <span className={preview ? (hasDiscount ? 'ca-gear-price is-sale' : 'ca-gear-price') : 'text-base font-bold text-gray-900'}>
              {formatPrice(product.price)}
            </span>
          )}
          {hasDiscount && (
            <span className="text-xs text-gray-400 line-through">
              {formatPrice(product.compareAtPrice)}
            </span>
          )}
        </div>
      </div>
    </a>
    <PreviewAddToCart
      className={preview ? 'ca-gear-atc' : 'px-3 pb-3'}
      compact
      handle={product.handle}
      title={product.title}
      image={product.imageUrl}
    />
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────

interface ShopGearCTAProps {
  features: Feature[];
  products?: ShopProduct[];
  entityId?: number;
  entityName?: string;
  entityType?: string;
}

const ShopGearCTA: React.FC<ShopGearCTAProps> = ({ features, products, entityId, entityName, entityType }) => {
  const profile = useMemo(() => getGearProfile(features, entityType), [features, entityType]);
  const hasProducts = products && products.length > 0;
  const preview = useCaPreview();

  // ca_preview: the gear moves out of the sidebar into the store rail in the
  // page's main column (StoreGearRail on the campsite page).
  const campsiteType = useMemo(() => {
    const descs = new Set(features.map((f) => (f.desc || '').toLowerCase().trim()));
    if (descs.has('caravan parks')) return 'caravan';
    if (descs.has('free camping')) return 'free_camping';
    if (descs.has('camping')) return 'camping';
    return 'general';
  }, [features]);

  if (preview) return null;

  return (
    <div className={preview
      ? 'ca-gear-panel bg-white rounded-md border border-[rgba(30,42,68,0.12)] p-6'
      : 'bg-gradient-to-br from-[#e3f2fd] to-indigo-50 rounded-lg border border-[#e3f2fd] p-6'}>
      <div className="flex items-center gap-2 mb-1">
        <FaShoppingBag className="text-[#1558b0]" />
        <h3 className="text-base font-semibold text-gray-900">
          {profile.heading}
        </h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">{profile.description}</p>

      {hasProducts ? (
        <>
          {/* Real product cards in a 2-column grid */}
          <div className="grid grid-cols-2 gap-3">
            {products!.slice(0, 4).map((product) => (
              <ProductCard
                key={product.handle}
                product={product}
                onTrack={() => trackProductClick(product, entityId, entityName, campsiteType)}
                preview={preview}
              />
            ))}
          </div>

          {/* Browse all link */}
          {preview ? (
            // ca_preview: the planner sits on the store domain, so the shop is
            // the same site: same tab, no external-link icon.
            <a href="/collections/all" className="ca-gear-all">
              Shop all camping gear
              <span aria-hidden="true">&rarr;</span>
            </a>
          ) : (
          <a
            href={`${STORE_URL}/collections`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 mt-4 py-2.5 bg-[#1558b0] text-white rounded-lg hover:bg-[#1558b0] transition-colors text-sm font-medium"
          >
            Browse all gear
            <FaExternalLinkAlt size={11} />
          </a>
          )}
        </>
      ) : (
        <>
          {/* Fallback: collection category links */}
          <div className="flex flex-col gap-2">
            {profile.links.map((link) => (
              <a
                key={link.href}
                href={preview ? link.href.replace(STORE_URL, '') : link.href}
                target={preview ? undefined : '_blank'}
                rel={preview ? undefined : 'noopener noreferrer'}
                className="flex items-center gap-3 px-3 py-2.5 bg-white rounded-lg border border-gray-100 hover:border-[#e3f2fd] hover:shadow-sm transition-all text-sm font-medium text-gray-700 hover:text-[#1558b0]"
              >
                {link.icon}
                <span>{link.label}</span>
                <span className="ml-auto text-gray-400 text-xs">&rarr;</span>
              </a>
            ))}
          </div>

          <a
            href={preview ? '/collections' : `${STORE_URL}/collections`}
            target={preview ? undefined : '_blank'}
            rel={preview ? undefined : 'noopener noreferrer'}
            className="block mt-3 text-center text-sm text-[#1558b0] hover:text-[#123f7d] font-medium"
          >
            Browse all gear &rarr;
          </a>
        </>
      )}
    </div>
  );
};

export default ShopGearCTA;
